<template>
  <article>
    <LoaderStack v-if="isLoading" />
    <template v-else>
      <AreaPricingBanner />

      <TheNavigation
        :trigger-id="triggerId"
        :navigation="navigation"
        :is-show-mobile-menu="isShowMobileMenu"
        @click-menu-button="isShowMobileMenu = !isShowMobileMenu"
      >
        <template #items-side>
          <li class="border-b-4 border-solid border-transparent px-2.5">
            <TheNuxtLink
              :to="`/${$i18n.locale}/marketplace/products/${$route.params.id}`"
              :title="productName"
              class="flex items-center whitespace-nowrap py-1 text-sm font-bold hover:text-blue-400"
            >
              <span class="leading-6">{{ productName }}</span>
              <IconArrow direction="right" class="ml-1" />
            </TheNuxtLink>
          </li>
        </template>
        <template #right-side>
          <TheButton
            :id="isCN ? 'tracking_form' : undefined"
            :to="`/${$i18n.locale}/marketplace/pricing/${$route.params.id}/contact-sales`"
            :title="tGlobal('pricing.request-a-quote')"
            class="w-[110px] tablet:w-[180px]"
          >
            <span class="text-sm">
              {{ tGlobal('pricing.request-a-quote') }}
            </span>
          </TheButton>
        </template>
      </TheNavigation>

      <TheScrollTrigger :offset="offset" @trigger="handleTrigger('overview')">
        <AreaPricingOverview id="overview" />
      </TheScrollTrigger>

      <TheScrollTrigger :offset="offset" @trigger="handleTrigger('pricing')">
        <AreaPricingPricePlans id="pricing" />
      </TheScrollTrigger>

      <TheScrollTrigger :offset="offset" @trigger="handleTrigger('info')">
        <AreaPricingAdditionalInformation id="info" />
      </TheScrollTrigger>

      <NewsletterSection />

      <TheNavigationMenu
        :navigation="navigation"
        :is-show-mobile-menu="isShowMobileMenu"
        @click-item-button="isShowMobileMenu = false"
      >
        <template #items-side>
          <li class="border-b border-solid border-gray-400 last:border-none">
            <TheNuxtLink
              class="flex w-full items-center py-3 font-bold"
              :to="`/${$i18n.locale}/marketplace/products/${$route.params.id}`"
              :title="productName"
            >
              <span class="leading-6">{{ productName }}</span>
              <IconArrow direction="right" class="ml-1" />
            </TheNuxtLink>
          </li>
        </template>
      </TheNavigationMenu>

      <AreaPricingPopUpDataCenter />
      <AreaPricingPopUpPlanUnavailable />
    </template>
  </article>
</template>

<script setup lang="ts">
  definePageMeta({
    middleware: ['pricing-plan-redirect'],
  });

  const route = useRoute();
  const { t } = useI18n({ useScope: 'local' });
  const { t: tGlobal } = useI18n({ useScope: 'global' });
  const { isCN } = useLanguage();
  const { headerHeight, navigationHeight, provideWebTitle } = useWebsite();
  const { arrived } = useScrollArrivedBottom();
  const { handleScrollTo } = useScrollTo();

  const pricingStore = usePricingStore();

  const { productName, isShowMobileMenu } = storeToRefs(pricingStore);

  const { status: GET_PRODUCT_DETAIL_STATUS } =
    await pricingStore.USE_GET_PRODUCT_DETAIL();

  const { status: GET_PRICING_DETAIL_STATUS } =
    await pricingStore.USE_GET_PRICING_DETAIL();

  const { status: GET_PRICING_PLANS_STATUS } =
    await pricingStore.USE_GET_PRICING_PLANS();

  const triggerId = ref<string>('');

  const offset = computed(
    (): number => headerHeight.value + navigationHeight.value
  );

  const navigation = computed((): { id: string; name: string }[] => {
    return [
      { id: 'overview', name: tGlobal('pricing.section-name.overview') },
      { id: 'pricing', name: tGlobal('pricing.section-name.pricing') },
      { id: 'info', name: tGlobal('pricing.section-name.info') },
    ];
  });

  const isLoading = computed((): boolean => {
    const result =
      GET_PRODUCT_DETAIL_STATUS.value === 'success' &&
      GET_PRICING_DETAIL_STATUS.value === 'success' &&
      GET_PRICING_PLANS_STATUS.value === 'success';
    return !result;
  });

  const handleTrigger = (id: string): void => {
    if (!arrived.value) {
      triggerId.value = id;
    }
  };

  const handleScrollToPricingSection = async (): Promise<void> => {
    // 若 URL Hash 為 #free-trial-plan 則滾動到定價方案區塊
    if (route.hash === '#free-trial-plan') {
      await nextTick(() => {
        handleScrollTo('pricing', offset.value);
      });
    }
  };

  watch(arrived, (value) => {
    // 當滾動到最底部時，將 triggerId 設為目前最後一個導航項目的 id
    if (value) {
      const id = navigation.value[navigation.value.length - 1]?.id || '';
      triggerId.value = id;
    }
  });

  watch(
    [GET_PRICING_PLANS_STATUS],
    (value) => {
      // 若請求失敗則顯示 404 頁面
      if (value.includes('error')) {
        showError({ statusCode: 404 });
      }
    },
    { immediate: true }
  );

  watch(
    isLoading,
    async (value) => {
      if (!value) {
        await handleScrollToPricingSection();
      }
    },
    { immediate: true }
  );

  onMounted(async () => {
    await handleScrollToPricingSection();
  });

  onBeforeMount(() => {
    pricingStore.RESET_DATA_CENTER();
  });

  useSeoMeta({
    title: () => t('seo-title', { productName: productName.value }),
    ogTitle: () =>
      provideWebTitle(t('seo-title', { productName: productName.value })),
    twitterTitle: () =>
      provideWebTitle(t('seo-title', { productName: productName.value })),
    description: () => t('seo-description', { productName: productName.value }),
    ogDescription: () =>
      t('seo-description', { productName: productName.value }),
    twitterDescription: () =>
      t('seo-description', { productName: productName.value }),
  });
</script>

<i18n lang="json">
{
  "en-int": {
    "seo-title": "{productName} Pricing",
    "seo-description": "Explore pricing options for {productName}. Compare plans and find the right configuration to support your business growth."
  },
  "en-jp": {
    "seo-title": "{productName} Pricing",
    "seo-description": "Explore pricing options for {productName}. Compare plans and find the right configuration to support your business growth."
  },
  "en-tw": {
    "seo-title": "{productName} Pricing",
    "seo-description": "Explore pricing options for {productName}. Compare plans and find the right configuration to support your business growth."
  },
  "en-kr": {
    "seo-title": "{productName} Pricing",
    "seo-description": "Explore pricing options for {productName}. Compare plans and find the right configuration to support your business growth."
  },
  "en-americas": {
    "seo-title": "{productName} Pricing",
    "seo-description": "Explore pricing options for {productName}. Compare plans and find the right configuration to support your business growth."
  },
  "en-asia-pacific": {
    "seo-title": "{productName} Pricing",
    "seo-description": "Explore pricing options for {productName}. Compare plans and find the right configuration to support your business growth."
  },
  "en-emea": {
    "seo-title": "{productName} Pricing",
    "seo-description": "Explore pricing options for {productName}. Compare plans and find the right configuration to support your business growth."
  },
  "ja-jp": {
    "seo-title": "{productName} 価格・料金プラン",
    "seo-description": "{productName} の料金プランをご確認ください。プランを比較し、貴社の成長を支える最適な構成を見つけましょう。"
  },
  "zh-cn": {
    "seo-title": "{productName} 价格方案",
    "seo-description": "了解 {productName} 的价格方案，选择最符合您企业成长需求的配置。"
  },
  "zh-tw": {
    "seo-title": "{productName} 價格方案",
    "seo-description": "了解 {productName} 的價格方案，選擇最符合您企業成長需求的配置。"
  }
}
</i18n>
